import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Sun, Moon, BookOpen, Calendar } from "lucide-react";
import { toast } from "sonner";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { usePeriod } from "@/hooks/usePeriod";
import MiniCalendar from "@/components/MiniCalendar";



const DiaryPage = () => {
  const { periodStartDate } = usePeriod();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [datesWithEntries, setDatesWithEntries] = useState([]);
  const [diary, setDiary] = useState({
    let_go: "",
    improve: "",
    enjoy: "",
    learned: "",
    positive_1: "",
    positive_2: "",
    positive_3: "",
    free_notes: ""
  });

  useEffect(() => {
    fetchDatesWithEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDiary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchDatesWithEntries = async () => {
    try {
      const response = await api.get('/diary/dates/with-entries');
      setDatesWithEntries(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching dates:", error);
    }
  };

  const fetchDiary = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/diary/${selectedDate}`);
      if (response.data && response.data.date) {
        setDiary({
          let_go: response.data.let_go || "",
          improve: response.data.improve || "",
          enjoy: response.data.enjoy || "",
          learned: response.data.learned || "",
          positive_1: response.data.positive_1 || "",
          positive_2: response.data.positive_2 || "",
          positive_3: response.data.positive_3 || "",
          free_notes: response.data.free_notes || ""
        });
      } else {
        setDiary({
          let_go: "",
          improve: "",
          enjoy: "",
          learned: "",
          positive_1: "",
          positive_2: "",
          positive_3: "",
          free_notes: ""
        });
      }
    } catch (error) {
      setDiary({
        let_go: "",
        improve: "",
        enjoy: "",
        learned: "",
        positive_1: "",
        positive_2: "",
        positive_3: "",
        free_notes: ""
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.post(`/diary`, {
        date: selectedDate,
        ...diary
      });
      toast.success("Diario salvato!");
      // Refresh dates with entries after saving
      fetchDatesWithEntries();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const formatDate = () => {
    return new Date(selectedDate).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const updateField = (field, value) => {
    setDiary(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="diary-page">
      {/* Period Banner */}
      {periodStartDate && (
        <div className="bg-muted/50 border border-border rounded-sm p-3 flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            Visualizzando dati dal <span className="font-medium text-foreground">{new Date(periodStartDate).toLocaleDateString('it-IT')}</span>
          </span>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="page-title">Diario</h1>
        <p className="page-subtitle">Riflessioni di inizio e fine giornata</p>
      </div>

      {/* Layout: Calendar + Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Mini Calendar Sidebar */}
        <div className="lg:col-span-1">
          <MiniCalendar
            datesWithEntries={datesWithEntries}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
          <p className="text-xs text-gray-500 text-center mt-3">
            {datesWithEntries.length} giorni con appunti
          </p>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Date Navigation */}
          <div className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm border border-gray-100">
            <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)} data-testid="prev-day-btn">
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Giorno prec.</span>
            </Button>
            <span className="font-heading text-sm sm:text-lg capitalize">{formatDate()}</span>
            <Button variant="ghost" size="sm" onClick={() => navigateDate(1)} data-testid="next-day-btn">
              <span className="hidden sm:inline mr-1">Giorno succ.</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
            </div>
          ) : (
            <div className="space-y-6">
          {/* Diario di Inizio Giornata */}
          <Card className="border border-border shadow-sm rounded-sm" data-testid="morning-diary">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Sun className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                </div>
                Diario di Inizio Giornata
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Cosa lascio andare oggi */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  • Cosa lascio andare oggi?
                </label>
                <Textarea
                  data-testid="let-go-input"
                  value={diary.let_go}
                  onChange={(e) => updateField("let_go", e.target.value)}
                  placeholder="Pensieri, preoccupazioni o tensioni che voglio rilasciare..."
                  className="rounded-sm min-h-[80px]"
                />
              </div>

              {/* Cosa voglio migliorare */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  • Che cosa voglio migliorare oggi?
                </label>
                <Textarea
                  data-testid="improve-input"
                  value={diary.improve}
                  onChange={(e) => updateField("improve", e.target.value)}
                  placeholder="Un aspetto su cui concentrarmi per crescere..."
                  className="rounded-sm min-h-[80px]"
                />
              </div>

              {/* Cosa voglio godermi */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  • Cosa voglio godermi oggi?
                </label>
                <Textarea
                  data-testid="enjoy-input"
                  value={diary.enjoy}
                  onChange={(e) => updateField("enjoy", e.target.value)}
                  placeholder="Momenti, attività o piccoli piaceri da assaporare..."
                  className="rounded-sm min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Diario di Fine Giornata */}
          <Card className="border border-border shadow-sm rounded-sm" data-testid="evening-diary">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Moon className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
                </div>
                Diario di Fine Giornata
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Cosa ho imparato */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  • Cosa ho imparato oggi?
                </label>
                <Textarea
                  data-testid="learned-input"
                  value={diary.learned}
                  onChange={(e) => updateField("learned", e.target.value)}
                  placeholder="Una lezione, un'intuizione o qualcosa di nuovo..."
                  className="rounded-sm min-h-[80px]"
                />
              </div>

              {/* 3 cose positive */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  • 3 cose positive accadute oggi
                </label>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-medium mt-2">1</span>
                    <Textarea
                      data-testid="positive-1-input"
                      value={diary.positive_1}
                      onChange={(e) => updateField("positive_1", e.target.value)}
                      placeholder="Prima cosa positiva..."
                      className="rounded-sm min-h-[60px] flex-1"
                    />
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-medium mt-2">2</span>
                    <Textarea
                      data-testid="positive-2-input"
                      value={diary.positive_2}
                      onChange={(e) => updateField("positive_2", e.target.value)}
                      placeholder="Seconda cosa positiva..."
                      className="rounded-sm min-h-[60px] flex-1"
                    />
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-medium mt-2">3</span>
                    <Textarea
                      data-testid="positive-3-input"
                      value={diary.positive_3}
                      onChange={(e) => updateField("positive_3", e.target.value)}
                      placeholder="Terza cosa positiva..."
                      className="rounded-sm min-h-[60px] flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Diario Libero */}
          <Card className="border border-border shadow-sm rounded-sm" data-testid="free-diary">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                </div>
                Diario Libero
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                data-testid="free-notes-input"
                value={diary.free_notes}
                onChange={(e) => updateField("free_notes", e.target.value)}
                placeholder="Scrivi liberamente i tuoi pensieri, riflessioni, emozioni o qualsiasi cosa tu voglia annotare..."
                className="rounded-sm min-h-[150px]"
              />
            </CardContent>
          </Card>

          <Button onClick={handleSave} data-testid="save-diary-btn" className="rounded-sm">
            Salva Diario
          </Button>
        </div>
          )}
        </div>
      </div>

      <DisclaimerFooter />
    </div>
  );
};

export default DiaryPage;
